import type { ReactNode } from 'react';
import { cn } from './cn';

/**
 * Large serif tab nav. The page title IS the navigation: each tab is set
 * in the display serif at 22px, muted-by-default, fully-toned + weight-
 * bumped when selected.
 *
 * Headless on purpose — caller passes the items array and an `activeId`.
 * Click handler is per-item so consumers wire it to react-router or a
 * controlled state machine.
 */
export interface TabNavItem {
  id: string;
  label: string;
  /** Optional adornment after the label (e.g., a count chip). */
  trailing?: ReactNode;
  disabled?: boolean;
}

export interface TabNavProps {
  items: TabNavItem[];
  activeId: string;
  onChange: (id: string) => void;
  className?: string;
  /** ARIA label for the tablist landmark. */
  ariaLabel?: string;
}

export function TabNav({
  items,
  activeId,
  onChange,
  className,
  ariaLabel = 'Primary navigation',
}: TabNavProps) {
  return (
    <div role="tablist" aria-label={ariaLabel} className={cn('flex items-end gap-8', className)}>
      {items.map((item) => {
        const isActive = item.id === activeId;
        return (
          <button
            key={item.id}
            type="button"
            role="tab"
            aria-selected={isActive}
            aria-controls={`tabpanel-${item.id}`}
            disabled={item.disabled}
            onClick={() => onChange(item.id)}
            className={cn(
              'inline-flex items-center gap-2 border-b-2 border-transparent pb-1 font-display text-display-md leading-none tracking-tight transition-colors duration-200 ease-out focus-visible:outline-none focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-ring',
              isActive ? 'font-medium text-fg' : 'text-fg-3 hover:text-fg-2',
              item.disabled && 'cursor-not-allowed opacity-50',
            )}
          >
            <span>{item.label}</span>
            {item.trailing ? <span aria-hidden>{item.trailing}</span> : null}
          </button>
        );
      })}
    </div>
  );
}
